// Settings card (office and admin, Session AUA, email system Phase 3, Item 10; Director decision DE-3; branded HTML since
// Session AUC): the company signature.
//
// ONE signature goes on every reply the platform drafts (the email engine's drafts, template drafts and Enquiry sends),
// whichever of our addresses the reply goes from, and never a person's name. Since Session AUC each reply carries it twice:
// a branded HTML signature (dark panel, the ELE logo, the contact rows in the brand colours) and the same words as plain text
// for a client that shows only text. Three things are the office's to change: the headline, the small print lines, and
// whether the logo is animated. The phone, WhatsApp and website rows and the design are fixed.
//
// Every change is previewed first: the HTML exactly as a customer receives it (the real image URL), in a light and a dark
// mail client, the plain-text version, and any check it fails. Then it is saved through a two-step confirm. "Use the default"
// previews the default the same way. Audited server side. The preview frames are sandboxed (no script) and the HTML is the
// server's own, built from escaped fields.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { CompanySignatureChange, CompanySignatureFailure, CompanySignatureFields, CompanySignatureSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";

type Pending = {
  change: CompanySignatureChange;
  fields: CompanySignatureFields;
  ok: boolean;
  failures: CompanySignatureFailure[];
  sample: string;
  sampleHtml: string;
  isDefault: boolean;
};

const preCls = "whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong";
const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";

// How each preview frame paints a mail client: the reader's own background and text colour. The signature panel is dark in
// both, as it is in the real thing; the reply's body text follows the client.
const CLIENTS = [
  { key: "light", label: "Light mail client", background: "#ffffff", color: "#1a1a1a" },
  { key: "dark", label: "Dark mail client", background: "#1f1f1f", color: "#e8e8e8" },
] as const;

// Painted with HTML attributes (bgcolor, text, cellpadding), never a style attribute: this file is client source, and the
// Content Security Policy rule keeps markup style attributes out of it (contentSecurityPolicy.sources.test.ts). The
// signature's own inline styles come from the server, as they do in the email itself.
function clientDocument(html: string, client: (typeof CLIENTS)[number]): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8"></head><body bgcolor="${client.background}" text="${client.color}" topmargin="0" leftmargin="0">` +
    `<table role="presentation" width="100%" cellpadding="16" cellspacing="0" border="0"><tr><td>${html}</td></tr></table></body></html>`
  );
}

/** The reply ending as each mail client shows it. */
export function SignaturePreview({ html, text, testId }: { html: string; text: string; testId: string }) {
  return (
    <div className="space-y-3" data-testid={testId}>
      {/* One client per row, each frame at least as wide as the signature (about 585px plus the reader's own padding), inside a
          wrapper that scrolls on a narrow window. Two frames side by side were each narrower than the signature, so the
          document overflowed its frame and was cut off or scrolled sideways, which read as the preview being clipped. */}
      <div className="grid gap-3">
        {CLIENTS.map((c) => (
          <div key={c.key}>
            <p className={labelCls}>{c.label}</p>
            <div data-hscroll className="overflow-x-auto">
              <iframe
                title={`The signature in a ${c.key} mail client`}
                sandbox=""
                srcDoc={clientDocument(html, c)}
                className="h-[290px] w-full min-w-[660px] rounded-ele border border-ele-hairline"
                data-testid={`${testId}-${c.key}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div>
        <p className={labelCls}>Plain text (a mail client that shows text only)</p>
        <pre className={preCls} data-testid={`${testId}-text`}>
          {text}
        </pre>
      </div>
    </div>
  );
}

export function CompanySignatureSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<CompanySignatureSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [headline, setHeadline] = useState("");
  const [smallPrint, setSmallPrint] = useState("");
  const [animated, setAnimated] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.settings.companySignature.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function preview(change: CompanySignatureChange) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    setPending(null);
    try {
      const res = await api.settings.companySignature.put({ ...change, preview: true });
      if (res.preview) {
        setPending({ change, fields: res.fields, ok: res.ok, failures: res.failures, sample: res.sample, sampleHtml: res.sampleHtml, isDefault: res.isDefault });
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the signature. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending || !pending.ok) return;
    setBusy("save");
    setError(null);
    try {
      const res = await api.settings.companySignature.put({ ...pending.change, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage(pending.change.useDefault ? "The default signature is in use again." : "Saved. Every reply drafted from now on carries this signature.");
        setPending(null);
        setEditing(false);
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the signature. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  function startEditing() {
    if (!view) return;
    const editFrom = view.storedFields ?? view.fields; // what is saved, even when it now fails and the default stands in
    setHeadline(editFrom.headline);
    setSmallPrint(editFrom.smallPrint.join("\n"));
    setAnimated(editFrom.animated);
    setPending(null);
    setError(null);
    setMessage(null);
    setEditing(true);
  }

  const edited = () => setPending(null);
  const typedChange = (): CompanySignatureChange => ({
    headline,
    smallPrint: smallPrint.split("\n").map((l) => l.trim()).filter(Boolean),
    animated,
    preview: true,
  });

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Company signature">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Company signature</h2>
      <p className="mb-3 text-sm text-ele-body">
        One signature goes on every reply the platform drafts, whichever of our addresses it is sent from, and it never names a person. Each reply
        carries it as a branded signature with the ELE logo, and as plain text for mail clients that show text only.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the signature.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-body">{view.label || "Company signature"} on every draft</p>
            {!pending && <SignaturePreview html={view.sampleHtml} text={view.text} testId="company-signature-current" />}
            <p className="mt-1 text-xs text-ele-body">
              {view.isDefault ? "This is the default signature." : "This is the office's own signature."}
              {view.fields.animated ? " The logo is animated." : " The logo is still."}
              {view.updatedAt ? ` Last changed ${formatDateTime(view.updatedAt)}${view.updatedByName ? ` by ${view.updatedByName}` : ""}.` : ""}
            </p>
          </div>
          {view.storedRefused && (
            <p role="alert" className="rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text)]">
              The saved signature no longer passes the checks, so the default is being used instead: {view.storedRefused}
            </p>
          )}

          {!editing ? (
            <button type="button" onClick={startEditing} className={linkBtn}>
              Change the signature
            </button>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Headline</span>
                <input
                  aria-label="Headline"
                  value={headline}
                  maxLength={view.limits.headlineMax}
                  onChange={(e) => {
                    setHeadline(e.target.value);
                    edited();
                  }}
                  className={fieldCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Small print (one line each, at most {view.limits.smallPrintMaxLines})</span>
                <textarea
                  aria-label="Small print"
                  value={smallPrint}
                  rows={4}
                  onChange={(e) => {
                    setSmallPrint(e.target.value);
                    edited();
                  }}
                  className={fieldCls}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-ele-strong">
                <input
                  type="checkbox"
                  aria-label="Animated logo"
                  checked={animated}
                  onChange={(e) => {
                    setAnimated(e.target.checked);
                    edited();
                  }}
                />
                Animated logo (desktop Outlook shows the first frame, a still logo)
              </label>
              <p className="text-xs text-ele-body">
                The phone (0203 576 4760), WhatsApp and website rows are fixed. No personal name, no other phone number, and only the registered address
                if you show an address. &quot;Kind regards,&quot; is added above the signature on every draft.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void preview(typedChange())}
                  disabled={busy != null || !headline.trim()}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:text-ele-green disabled:opacity-50"
                >
                  {busy === "preview" ? "Checking..." : "Preview"}
                </button>
                {!view.isDefault && (
                  <button type="button" onClick={() => void preview({ useDefault: true, preview: true })} disabled={busy != null} className={linkBtn}>
                    Use the default
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setPending(null);
                    setError(null);
                  }}
                  disabled={busy === "save"}
                  className={linkBtn}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {pending && (
            <div className="rounded-ele border border-ele-hairline p-3" data-testid="company-signature-preview">
              <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-body">
                {pending.isDefault ? "The default signature" : "The new signature"}: how a reply ends with it
              </p>
              <SignaturePreview html={pending.sampleHtml} text={pending.sample} testId="company-signature-new" />
              {pending.failures.length > 0 ? (
                <div className="mt-2" role="alert">
                  <p className="text-sm font-semibold text-ele-danger">It cannot be saved yet:</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {pending.failures.map((f, i) => (
                      <li key={`${f.check}-${i}`} className="text-ele-body">
                        {f.line ? <span className="text-ele-strong">&quot;{f.line}&quot;: </span> : null}
                        {f.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="mt-3">
                  <ConfirmButton
                    onConfirm={() => void save()}
                    label={pending.isDefault && pending.change.useDefault ? "Use the default signature" : "Save this signature"}
                    confirmLabel="Every new draft will carry it. Press again"
                    busy={busy === "save"}
                    busyLabel="Saving..."
                    disabled={busy != null}
                    className="rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green"
                    armedClassName="!text-ele-green font-semibold"
                  />
                </div>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
